// Country-aware emergency numbers for the Signal Journal's urgent panel
// (Council Decision B, 2026-10-01). The Warrior can always change the country;
// nothing about their location is detected or stored beyond this browser.
//
// Source: Wikipedia "List of emergency telephone numbers", checked 2026-10-01.
// Where a country lists both a general and an ambulance number, `number` is the
// one to dial for a medical emergency and `alt` is the other. Pending clinical
// reviewer sign-off with the rest of the urgent panel.

export const EMERGENCY_NUMBERS = Object.freeze({
  US: { name: "United States", number: "911" },
  CA: { name: "Canada", number: "911" },
  PR: { name: "Puerto Rico", number: "911" },
  GB: { name: "United Kingdom", number: "999", alt: "112" },
  IE: { name: "Ireland", number: "112", alt: "999" },
  JM: { name: "Jamaica", number: "110", alt: "119 (police)" },
  TT: { name: "Trinidad and Tobago", number: "811" },
  BB: { name: "Barbados", number: "511" },
  BS: { name: "Bahamas", number: "911", alt: "919" },
  DO: { name: "Dominican Republic", number: "911" },
  HT: { name: "Haiti", number: "116" },
  NG: { name: "Nigeria", number: "112" },
  GH: { name: "Ghana", number: "112", alt: "193 (ambulance)" },
  KE: { name: "Kenya", number: "112", alt: "999" },
  UG: { name: "Uganda", number: "112" },
  TZ: { name: "Tanzania", number: "114" },
  CM: { name: "Cameroon", number: "119" },
  ZA: { name: "South Africa", number: "10177", alt: "112 from a mobile phone" },
  IN: { name: "India", number: "112", alt: "108 (ambulance)" },
  BR: { name: "Brazil", number: "192" },
  FR: { name: "France", number: "15", alt: "112" },
  DE: { name: "Germany", number: "112" },
  NL: { name: "Netherlands", number: "112" },
  BE: { name: "Belgium", number: "112" },
  IT: { name: "Italy", number: "112" },
  ES: { name: "Spain", number: "112" },
  SA: { name: "Saudi Arabia", number: "997" },
  AE: { name: "United Arab Emirates", number: "998" },
  AU: { name: "Australia", number: "000" },
  NZ: { name: "New Zealand", number: "111" },
});

export const OTHER_COUNTRY = "OTHER";
export const LOCAL_EMERGENCY_FALLBACK = "Contact your local emergency service";

const STORAGE_KEY = "wip_emergency_country";

// Best guess from the browser's language setting (e.g. "en-GB" -> "GB").
// A guess only: the Warrior sees it and can change it.
export function guessCountry(languages) {
  const list = languages || (typeof navigator !== "undefined" ? navigator.languages || [navigator.language] : []);
  for (const tag of list || []) {
    const m = /^[a-z]{2,3}[-_]([A-Za-z]{2})\b/.exec(String(tag || ""));
    if (m && EMERGENCY_NUMBERS[m[1].toUpperCase()]) return m[1].toUpperCase();
  }
  return OTHER_COUNTRY;
}

export function loadCountry() {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v && (v === OTHER_COUNTRY || EMERGENCY_NUMBERS[v])) return v;
  } catch (e) { /* storage blocked: fall back to the guess */ }
  return guessCountry();
}

export function saveCountry(code) {
  try { window.localStorage.setItem(STORAGE_KEY, code); } catch (e) { /* ignore */ }
}

// What the urgent panel shows for a country code.
export function emergencyFor(code) {
  const c = EMERGENCY_NUMBERS[code];
  if (!c) return { code: OTHER_COUNTRY, number: null, label: LOCAL_EMERGENCY_FALLBACK };
  return { code, number: c.number, alt: c.alt || null, label: `Call ${c.number}`, name: c.name };
}

export const telHref = (number) => `tel:${String(number).replace(/[^0-9+]/g, "")}`;
